import React, { useState } from 'react';

import Router from 'next/router';
import { useSelector } from 'react-redux';
import styled, { css } from 'styled-components';

import { useMembership } from '@techstyle/react-accounts';
import { TextLink } from '@techstyle/react-components';
import { FormattedMessage, useIntl } from '@techstyle/react-intl';

import { desktop } from '../../styles';
import { BasicButton } from '../../styles/button';
import { BasicModal } from '../../styles/modal';
import { isDateFirstToFifthOfTheMonth } from '../../utils/selectors';
import { Component as Price } from '../ExtendedPrice';
import { Component as StaticIcon } from '../StaticIcon';
import { Component as XtraVipIcon } from '../XtraVipIcon';

const LockStaticIcon = styled(StaticIcon).attrs({
  type: 'lock',
  size: 14,
})`
  margin: 3px;
`;

const ByoNoCreditsWrapper = styled.div`
  padding: ${props => props.theme.spacing.small}px;
  background-color: ${props => props.theme.colors.lavender100};
  box-shadow: 0 2px 4px ${props => props.theme.colors.lightShadow1};
  border-radius: 4px;
`;

const ByoNoCreditsTitle = styled.div`
  display: flex;
  font-weight: 700;
  font-size: 14px;
  color: ${props => props.theme.colors.lavender600};
  letter-spacing: 0.3px;
`;

const ByoNoCreditsParagraph = styled.p`
  color: ${props => props.theme.colors.darkGrey};
  letter-spacing: 0.3px;
  padding: 4px 0px 0px 4px;
`;

const ByoNoCreditsLink = styled(TextLink)`
  color: ${props => props.theme.colors.lavender500};
`;

const dialogStyle = css`
  max-height: 500px;
  max-width: 366px;
  border-radius: 8px;

  ${desktop`
    max-height: auto;
    margin: 0 150px;
  `};
`;

const StyledXtraVipIcon = styled(XtraVipIcon)`
  width: 80px;
`;

const ModalContent = styled.div`
  align-items: center;
  justify-content: center;
  display: flex;
  flex-direction: column;
  padding: ${props => props.theme.spacing.moderate}px;
  overflow: hidden;
`;

const ModalTitle = styled.p`
  color: ${props => props.theme.colors.lavender600};
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 1.6px;
  padding-top: ${props => props.theme.spacing.small}px;
  text-align: center;
  text-transform: uppercase;
`;

const ListWrapper = styled.ul`
  list-style: disc;
  padding-left: ${props => props.theme.spacing.small}px;
`;

const ListItem = styled.li`
  color: ${props => props.theme.colors.darkLightBlack};
  line-height: 21px;
  list-style-position: outside;
  padding-top: ${props => props.theme.spacing.small}px;
  font-size: 14px;
`;

const ContinueButton = styled(BasicButton)`
  background-color: ${props => props.theme.colors.lavender600};
  border: ${props => props.theme.colors.lavender600};
  color: ${props => props.theme.colors.white};
  font-weight: 600;
  font-size: 14px;
  letter-spacing: 1.3px;
  min-width: 150px;
  margin-top: ${props => props.theme.spacing.moderate}px;
  text-transform: uppercase;
`;

export default function ByoNoCredits() {
  const { price: membershipPrice } = useMembership();
  const { formatMessage } = useIntl();
  const [isModalShown, setShowModal] = useState(false);
  const shouldShowClaimCreditButton = useSelector(isDateFirstToFifthOfTheMonth);

  const handleLearnMoreClick = e => {
    e.preventDefault();
    setShowModal(true);
  };

  const handleClose = () => {
    setShowModal(false);
  };

  const handleRedirect = () => {
    Router.push('/account/membership');
    handleClose();
  };

  const renderContinueButton = () => {
    return shouldShowClaimCreditButton ? (
      <ContinueButton onClick={handleRedirect}>
        <FormattedMessage
          id="site_pdp.i_want_my_member_credit"
          defaultMessage="I want my member credit"
        />
      </ContinueButton>
    ) : (
      <ContinueButton onClick={handleClose}>
        <FormattedMessage
          id="site_pdp.got_it_continue"
          defaultMessage="Got It"
        />
      </ContinueButton>
    );
  };

  const renderMemberCredtsModal = () => {
    return (
      <BasicModal
        isOpen={isModalShown}
        onExit={handleClose}
        dialogStyle={dialogStyle}
        title={formatMessage({
          id: 'site_pdp.build_your_own_modal_title_no_credits',
          defaultMessage: 'Member Credits & Xclusive Bundles',
        })}
      >
        <ModalContent>
          <StyledXtraVipIcon />
          <ModalTitle>
            <FormattedMessage
              id="site_pdp.member_credits_and_xclusive_bundles"
              defaultMessage="Member Credits and Xclusive Bundles"
            />
          </ModalTitle>
          <ListWrapper>
            <ListItem>
              <FormattedMessage
                id="site_pdp.take_your_membership_to_the_next"
                defaultMessage="Take your membership to the next level! On the 6th of each month, if you haven't chosen to ‘Skip the Month,’ your payment method will be charged {formattedMembershipPrice}, which earns you 1 Member Credit."
                values={{
                  formattedMembershipPrice: <Price amount={membershipPrice} />,
                }}
              />
            </ListItem>
            <ListItem>
              <FormattedMessage
                id="site_pdp.each_member_credit_can_be_redeemed"
                defaultMessage="Each Member Credit can be redeemed for ANY item, Xtra VIP box, Set, or Xclusive Bundle."
              />
            </ListItem>
            <ListItem>
              <FormattedMessage
                id="site_pdp.create_an_xclusive_bundle"
                defaultMessage="Create an Xclusive Bundle with any two items up to $39.95 each."
              />
            </ListItem>
            <ListItem>
              <FormattedMessage
                id="site_pdp.redeem_your_xclusive_bundle_for"
                defaultMessage="Redeem your Xclusive Bundle for 1 Member Credit at checkout."
              />
            </ListItem>
          </ListWrapper>
          {renderContinueButton()}
        </ModalContent>
      </BasicModal>
    );
  };

  return (
    <ByoNoCreditsWrapper>
      <ByoNoCreditsTitle>
        <LockStaticIcon />
        <FormattedMessage
          id="site_pdp.build_your_own_xclusive_bundle"
          defaultMessage="Build Your Own Xclusive Bundle"
        />
      </ByoNoCreditsTitle>
      <ByoNoCreditsParagraph>
        <FormattedMessage
          id="site_pdp.unlock_this_xclusive_perk_when_you_earn_paragraph"
          defaultMessage="Unlock this Xclusive Perk when you earn a Member Credit. {byoNoCreditsLink}"
          values={{
            byoNoCreditsLink: (
              <ByoNoCreditsLink onClick={handleLearnMoreClick}>
                <FormattedMessage
                  id="site_pdp.learn_more_byo"
                  defaultMessage="Learn More"
                />
              </ByoNoCreditsLink>
            ),
          }}
        />
      </ByoNoCreditsParagraph>
      {renderMemberCredtsModal()}
    </ByoNoCreditsWrapper>
  );
}
